import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Message } from '@deliveryhero/pd-cookbook/components/Message';
import { Typography } from '@deliveryhero/pd-cookbook/components/Typography';
import { Box, Container, Grid } from '@mui/material';

import AuthFooter from 'components/AuthFooter';
import { LoginForm } from 'components/Forms/LoginForm';
import { PasswordFormValues } from 'components/Forms/LoginForm/validation';
import { Logo } from 'components/Logo';
import { BrandCodes } from 'components/Theme/types';
import { useAuth } from 'contexts/authentication';
import { useConfiguration } from 'contexts/country-config';
import { AppRoute } from 'share/types/routes';

const PAGE_TITLE = 'Log in';
const PAGE_SUBTITLE = 'Welcome back to the employee benefits management portal';

type AlertColor = 'information' | 'error' | 'warning' | 'success';

export function LoginContainer() {
  const auth = useAuth();
  const navigate = useNavigate();
  const countryConfig = useConfiguration();
  const brandCode = countryConfig.entity.code.toLowerCase() as BrandCodes;

  const [searchParams] = useSearchParams();
  const [alertType] = useState<AlertColor>(
    (searchParams.get('type') as AlertColor) || 'error'
  );
  const [alertMessage, setAlertMessage] = useState<string>(
    searchParams.get('message') || ''
  );

  const handleSubmit = async (values: PasswordFormValues) => {
    return (
      auth.signIn &&
      auth
        .signIn(values.username, values.password)
        .then((res) => {
          if (res.two_factor_auth) {
            navigate(AppRoute.TwoFactorAuth, { replace: true });
          } else {
            navigate(AppRoute.Index, { replace: true });
          }
        })
        .catch((e: Error) => {
          setAlertMessage(e.message);
          throw e;
        })
    );
  };

  const initialValues = {
    username: '',
    password: '',
    showPasswordInput: true,
    showEmailInput: true
  };

  return (
    <Container disableGutters maxWidth={false}>
      <div
        style={{
          position: 'absolute',
          left: '-478px',
          top: '-497px'
        }}
      >
        <img
          src={`https://images.deliveryhero.io/image/fd-sg/corporate/company-admin/login-page.png`}
          style={{
            transform: 'rotate(329.49deg)'
          }}
        />
      </div>
      <Grid
        item
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          mt: '100px',
          pl: '390px'
        }}
      >
        <Box
          component="div"
          display="flex"
          flexDirection="column"
          alignItems="center"
          width="400px"
        >
          <Logo brandCode={brandCode} style={{ paddingBottom: '16px' }} />
          <Typography type="title-xl" color="neutral-primary" marginBottom="sm">
            {PAGE_TITLE}
          </Typography>
          <Typography
            type="paragraph-sm"
            color="neutral-primary"
            marginBottom="sm"
          >
            {PAGE_SUBTITLE}
          </Typography>
          {Boolean(alertMessage) && (
            <Message
              message={alertMessage}
              kind={alertType}
              style={{ marginBottom: '16px', width: '100%' }}
            />
          )}
          <LoginForm
            initialValues={initialValues}
            onSubmit={handleSubmit}
            buttonText="Log in"
          ></LoginForm>
        </Box>
        <AuthFooter
          contactEmail={countryConfig.contact_us}
          indexUrl={countryConfig.index}
          brand={countryConfig.entity.brand.name}
        />
      </Grid>
    </Container>
  );
}
